// /police-clearance/for/$country — Country page (TH, 2,500+ words)
import { policeCountrySectionsTh, policeHubSectionsTh } from "@/data/police-content";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import type { PoliceDeepThEntry } from "@/data/police-deep-th";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { TARGET_COUNTRIES, getTargetCountry } from "@/data/police-target-countries";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import PoliceTripleKeyLinks from "@/components/PoliceTripleKeyLinks";
import ApostilleCrossLinks from "@/components/ApostilleCrossLinks";
import { Globe2, MessageCircle, Phone, BadgeCheck, CheckCircle2, AlertTriangle, BookOpen, ListChecks, Calendar, ExternalLink } from "lucide-react";
import CountryDeepContent from "@/components/CountryDeepContent";
import { normalizeCountrySlug } from "@/lib/country-hague";


const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/police-clearance/for/$country")({
  loader: async ({ params }) => {
    if (!getTargetCountry(params.country)) throw notFound();
    const { getPoliceDeepTh } = await import("@/data/police-deep-th");
    const deep: PoliceDeepThEntry | null = getPoliceDeepTh(params.country) ?? null;
    return { deep, slug: params.country };
  },
  head: ({ params, loaderData }) => {
    const c = getTargetCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("ไม่พบประเทศ", "/police-clearance/for/$country", "th")}] };
    const deep = loaderData?.deep;
    const url = `${SITE}/police-clearance/for/${c.slug}`;
    const title = deep?.metaTitle ?? `ตรวจประวัติอาชญากรรม สำหรับยื่น${c.nameTh} | NYC Police Clearance`;
    const desc = deep?.metaDescription ?? `บริการตรวจประวัติอาชญากรรม PCSC สำหรับใช้ที่${c.nameTh} (${c.nameEn}) — แปล + ${c.hague ? "Apostille" : "MFA + รับรองสถานทูต"} ครบในแพ็กเกจเดียว เริ่ม ฿4,500 · วีซ่า: ${c.visaTypes.slice(0, 3).join(", ")}`;
    const faqs = deep?.faqs ?? [
      { q: `ตรวจประวัติสำหรับ${c.nameTh} ใช้เวลาเท่าไหร่?`, a: "15-25 วันทำการ ครบทุกขั้นตอน — PCSC + แปล + Legalization + จัดส่ง" },
      { q: `${c.nameTh} เป็นสมาชิก Hague Apostille หรือไม่?`, a: c.hague ? "ใช่ — ใช้ Apostille ของกระทรวงการต่างประเทศไทย ไม่ต้องรับรองสถานทูต" : `ไม่ใช่ — ต้องรับรองที่ ${c.embassyTh} หลัง MFA Legalization` },
      { q: `วีซ่าที่ต้องใช้ตรวจประวัติ?`, a: c.visaTypes.join(", ") },
    ];

    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance/for/$country", "th") },
        { name: "description", content: ctaDesc(desc, "/police-clearance/for/$country", "th")},
        { name: "keywords", content: `ตรวจประวัติอาชญากรรม ${c.nameTh}, Police Clearance ${c.nameEn}, PCSC ${c.nameTh}, ${c.visaTypes.join(", ")}` },
        { property: "og:title", content: ctaTitle(title, "/police-clearance/for/$country", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance/for/$country", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/police-clearance/for/${c.slug}`)],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Thai Police Clearance for ${c.nameEn}`,
              serviceType: "Police Clearance for Visa Application",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: c.nameEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 4500, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/police-clearance` },
                { "@type": "ListItem", position: 3, name: c.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: PoliceCountryPage,
});

function PoliceCountryPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getTargetCountry(slug)!;
  const deep = (Route.useLoaderData() as { deep: PoliceDeepThEntry | null }).deep;
  const sections = [...policeCountrySectionsTh(c), ...policeHubSectionsTh().slice(1, 3)];
  const related = TARGET_COUNTRIES.filter((x) => x.slug !== slug).slice(0, 9);


  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance" className="hover:text-gold">Police Clearance</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Globe2 className="h-3.5 w-3.5" /> สำหรับ {c.nameTh} ({c.nameEn})
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            ตรวจประวัติอาชญากรรม สำหรับยื่น{c.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            PCSC + แปล + {c.hague ? "Apostille (Hague Member)" : "MFA + Embassy Legalization"} — เริ่ม ฿4,500 · 15-25 วันทำการ
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {deep?.speakable ?? `ตรวจประวัติอาชญากรรมไทย (PCSC) สำหรับยื่นที่${c.nameTh} — รวมแปลและ ${c.hague ? "Apostille" : "Embassy Legalization"} เริ่ม ฿4,500 เสร็จ 15-25 วันทำการ`}
        </blockquote>

        {deep && (
          <div className="mt-10 space-y-10">
            {/* Overview */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
                <BookOpen className="h-5 w-5 text-amber-600" /> ภาพรวมเชิงลึก & กรอบกฎหมาย
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-700">{deep.overview}</p>
              <div className="mt-4 rounded-xl bg-amber-50/70 p-4 text-[13.5px] text-slate-800 ring-1 ring-amber-200">
                <span className="font-bold text-amber-800">⏱ Acceptance Window:</span> {deep.acceptanceWindow}
              </div>
            </section>

            {/* Pipeline */}
            <section>
              <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
                <ListChecks className="h-5 w-5 text-amber-600" /> ขั้นตอนการทำงาน (Step-by-Step)
              </h2>
              <ol className="mt-4 grid gap-3 sm:grid-cols-2">
                {deep.pipeline.map((p, i) => (
                  <li key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="font-display text-[14px] font-extrabold text-navy-deep">{p.step}</div>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{p.detail}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/* Visa Matrix */}
            <section>
              <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
                <Calendar className="h-5 w-5 text-amber-600" /> ตารางวีซ่า & อายุการใช้งาน
              </h2>
              <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-[13px]">
                  <thead className="bg-slate-50 text-[12px] uppercase tracking-wider text-slate-600">
                    <tr><th className="px-3 py-2">ประเภทวีซ่า</th><th className="px-3 py-2">ความจำเป็น</th><th className="px-3 py-2">อายุการใช้งาน</th></tr>
                  </thead>
                  <tbody>
                    {deep.visaMatrix.map((v, i) => (
                      <tr key={i} className="border-t border-slate-100 odd:bg-white even:bg-slate-50/50">
                        <td className="px-3 py-2.5 font-bold text-navy-deep">{v.visa}</td>
                        <td className="px-3 py-2.5 text-slate-700">{v.need}</td>
                        <td className="px-3 py-2.5 text-slate-700">{v.window}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Pitfalls */}
            <section>
              <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
                <AlertTriangle className="h-5 w-5 text-amber-600" /> ข้อผิดพลาดที่พบบ่อย (Common Pitfalls)
              </h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {deep.pitfalls.map((p, i) => (
                  <div key={i} className="rounded-xl border-l-4 border-amber-400 bg-amber-50/50 p-4">
                    <div className="font-bold text-amber-900">{p.title}</div>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate-700">{p.detail}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Citations */}
            <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-navy-deep">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> เอกสารอ้างอิงทางการ (E-E-A-T)
              </h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {deep.citations.map((cit, i) => (
                  <li key={i}>
                    <a href={cit.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-start gap-1.5 text-[12.5px] text-blue-700 hover:underline">
                      <ExternalLink className="mt-0.5 h-3 w-3 shrink-0" /> {cit.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}

        <div className="mt-10">
          <PoliceSections sections={sections} />
        </div>


        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `${c.nameTh} เป็น Hague Member ไหม?`, a: c.hague ? "ใช่ — ใช้ Apostille ของไทย" : `ไม่ใช่ — ต้องรับรองที่ ${c.embassyTh}` },
                { q: `วีซ่าที่ต้องใช้?`, a: c.visaTypes.join(", ") },
                { q: `ใช้เวลาเท่าไหร่?`, a: "รวมทุกขั้นตอน 15-25 วันทำการ" },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศปลายทางอื่นๆ</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/police-clearance/for/$country" params={{ country: r.slug }} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-slate-800">{r.nameTh}</span>
              </Link>
            ))}
          </div>
        </section>

        <PoliceTripleKeyLinks mode="th" countryNameTh={c.nameTh} countryNameEn={c.nameEn} />
        <ApostilleCrossLinks mode="th" source="police" sourceSlug={c.slug} countryLabel={c.nameTh} />
      </div>

      <CountryDeepContent
        lang="th"
        service="mfa"
        countryTh={c.nameTh}
        countryEn={c.nameEn}
        hague={c.hague}
        priceFromTh={4500}
        turnaround="15–25 วันทำการ"
        slug={normalizeCountrySlug(c.slug)}
      />

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  );
}
